import {
    addToChainAfter,
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    chain,
    PopulateApiParameter,
    SendDataToApi,
} from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import { CartLineCollectionModel, CartLineModel, OrderModel } from "@insite/client-framework/Types/ApiModels";
import { addToCartEvent } from "@WhiteCap/Common/Utillities/CustomTracking";

type ReorderParameter = {
    orderNumber: string;
    skipRestrictedItemsCheck?: boolean;
} & HasOnSuccess &
    HasOnError<string>;

export interface ReorderResult {
    order: OrderModel;
    cartLines?: CartLineCollectionModel;
}


type HandlerType = ApiHandlerDiscreteParameter<ReorderParameter, CartLineCollectionModel, ReorderResult | undefined>;

export const trackAddtoCartEvent: HandlerType = props => {
    const session = getSession(props.getState());
    const state = props.getState();
    const cart = getCurrentCartState(state).value; 
    const order = props.apiResult?.order
    const cartLines = order?.orderLines?.filter(line => line.unitNetPrice > 0 && line.qtyOrdered > 0);
    const totalValue = cartLines?.reduce((sum, item) => {
            return sum + (item.unitNetPrice * item.qtyOrdered);
    }, 0);
    if (cartLines && cart) {
        const newItem =cartLines.map(item => {
            return {
                item_name: item?.shortDescription ? item.shortDescription : item.description,
                item_id: item.productErpNumber,
                quantity: item.qtyOrdered,
                price: item.unitNetPrice,
                item_brand: item?.brand?.name,
            };
        });
        addToCartEvent({
            event: "add_to_cart",
            ecommerce: {
                currency: session?.currency?.currencyCode,
                value: totalValue,
                items: [...newItem],
            },
        });
    }
};

export const PopulateApiParameterExtended: HandlerType = props => {
    
    const orderLines = props.apiResult?.order.orderLines;

    const restrictedItems = orderLines
    ?.filter(line => line.canAddToCart === false)
    .map(line => ({
        productNumber: line.productErpNumber,
        title: line.shortDescription || line.description,
    }));

    if (!props.apiResult?.order.orderLines?.some(o => !!o.productId) || 
        props.apiResult?.order.orderLines?.filter(o => o.productId!=="00000000-0000-0000-0000-000000000000").length===0) {
        props.apiResult = undefined;
        props.dispatch({
            type: "Pages/OrderHistory/CompleteReorder",
            orderNumber: props.parameter.orderNumber,
        });
        props.dispatch({
            type: "Context/CompleteUpdatingCart",
        });
        props.parameter.onError?.(siteMessage("OrderHistory_NoProductsAvailableForReorder") as string);
        return false;
    }

    const hasRestrictedItems = orderLines?.some(line => line.canAddToCart === false);

    if (hasRestrictedItems && !props.parameter.skipRestrictedItemsCheck) {
        props.apiResult = undefined;
        props.dispatch({
            type: "Pages/OrderHistory/CompleteReorder",
            orderNumber: props.parameter.orderNumber,
        });
        props.dispatch({
            type: "Context/CompleteUpdatingCart",
        });
        props.parameter.onError?.(
            JSON.stringify({
                code: "RESTRICTED_ITEMS",
                items: restrictedItems,
            }),
        );

        return false;
    }

    props.apiParameter = {
        cartLines: props.apiResult.order.orderLines.map(
            line =>
                ({
                    productId: line.productId,
                    qtyOrdered: line.qtyOrdered <= 0 ? 1 : line.qtyOrdered,
                    unitOfMeasure: line.unitOfMeasure,
                } as CartLineModel),
        ),
    } as CartLineCollectionModel;
};


addToChainAfter(chain, SendDataToApi, trackAddtoCartEvent);
replaceInChain(chain, PopulateApiParameter, PopulateApiParameterExtended);